import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import {
  ServicesMasterSchedule,
  useDeleteApiMasterScheduleByScheduleIdMutation,
  useGetApiProjectsQuery,
  usePatchApiMasterScheduleByScheduleIdMutation,
  usePostApiMasterScheduleUploadMutation,
} from '../../../../app/athenaApiGen';
import DeleteConfirmation from '../../../../components/Modals/DeleteConfirmation';
import { StyledDataGrid } from '../../../../shared-theme/StyledComponents';
import {
  DEFAULT_TABLE_PAGE_SIZE,
  TABLE_PAGE_SIZE_OPTIONS,
} from '../../../../utils/tableVariables';
import { useMppContext } from '../context/MppContextEnhanced';

interface EditDialogProps {
  open: boolean;
  schedule: ServicesMasterSchedule | null;
  onClose: () => void;
  onSave: (id: string, name: string, projectId: string) => void;
}

const EditDialog: React.FC<EditDialogProps> = ({
  open,
  schedule,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState(schedule?.name || '');
  const [projectId, setProjectId] = useState(
    schedule?.projectData?.name ? '' : '',
  );
  const { data: projectsData, isLoading: projectsLoading } =
    useGetApiProjectsQuery({
      pageSize: 100,
      includeArchived: false,
    });

  const projects = (projectsData?.projects || []).filter((p) => p.workflowState === "Internally Approved");

  React.useEffect(() => {
    if (schedule) {
      setName(schedule.name || '');
      // Try to find current project association - projectId is at top level of schedule
      setProjectId((schedule as any).projectId || '');
    }
  }, [schedule]);

  const handleSave = () => {
    if (schedule?.id) {
      onSave(schedule.id, name, projectId);
    }
  };

  const handleProjectChange = (e: SelectChangeEvent<string>) => {
    setProjectId(e.target.value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit IMS Schedule</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            autoFocus
            label="Schedule Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FormControl fullWidth disabled={projectsLoading}>
            <InputLabel id="edit-project-select-label">
              Project Association
            </InputLabel>
            <Select
              labelId="edit-project-select-label"
              value={projectId}
              label="Project Association"
              onChange={handleProjectChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.projectId} value={project.projectId}>
                  {project.projectCode
                    ? `${project.title} (${project.projectCode})`
                    : project.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface OverwriteDialogProps {
  open: boolean;
  schedule: ServicesMasterSchedule | null;
  onClose: () => void;
  onUpload: (file: File) => void;
}

const OverwriteDialog: React.FC<OverwriteDialogProps> = ({
  open,
  schedule,
  onClose,
  onUpload,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleUpload = () => {
    if (selectedFile) {
      onUpload(selectedFile);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Replace IMS Schedule</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Upload a new .mpp file to replace "{schedule?.name}". The existing
          schedule data will be overwritten with the new file contents.
        </DialogContentText>
        <input
          accept=".mpp"
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ marginTop: '16px' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="warning"
          disabled={!selectedFile}
        >
          Replace
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface MppTableProps {
  onDeleteSuccess?: () => void;
  onUpdateSuccess?: () => void;
  onUpdateError?: () => void;
  onOverwriteSuccess?: () => void;
}

const MppTable: React.FC<MppTableProps> = ({
  onDeleteSuccess,
  onUpdateSuccess,
  onUpdateError,
  onOverwriteSuccess,
}) => {
  const { data: schedules, loading, refreshData } = useMppContext();
  const [deleteSchedule] = useDeleteApiMasterScheduleByScheduleIdMutation();
  const [uploadSchedule] = usePostApiMasterScheduleUploadMutation();
  const [updateSchedule] = usePatchApiMasterScheduleByScheduleIdMutation();

  // Fetch projects for ID-to-name mapping
  const { data: projectsData } = useGetApiProjectsQuery({
    pageSize: 100,
    includeArchived: false,
  });

  // Create lookup map for efficient project name resolution
  const projectNameMap = useMemo(() => {
    const map = new Map<string, { title: string; projectCode?: string }>();
    projectsData?.projects?.forEach((project) => {
      if (project.projectId) {
        map.set(project.projectId, {
          title: project.title || '',
          projectCode: project.projectCode,
        });
      }
    });
    return map;
  }, [projectsData]);

  // Helper to format project display name
  const getProjectDisplayName = (projectId?: string) => {
    if (!projectId) return 'Program Level';
    const project = projectNameMap.get(projectId);
    if (!project) return projectId;
    return project.projectCode
      ? `${project.title} (${project.projectCode})`
      : project.title;
  };

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] =
    useState<ServicesMasterSchedule | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [scheduleToEdit, setScheduleToEdit] =
    useState<ServicesMasterSchedule | null>(null);
  const [updating, setUpdating] = useState(false);

  // Overwrite dialog state
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [scheduleToOverwrite, setScheduleToOverwrite] =
    useState<ServicesMasterSchedule | null>(null);

  // Actions menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSchedule, setMenuSchedule] =
    useState<ServicesMasterSchedule | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filter schedules based on search query
  const filteredSchedules = useMemo(() => {
    if (!schedules || !searchQuery.trim()) {
      return schedules || [];
    }
    const query = searchQuery.toLowerCase();
    return schedules.filter((schedule) => {
      const projectName = getProjectDisplayName(
        schedule.projectId,
      ).toLowerCase();
      return (
        schedule.name?.toLowerCase().includes(query) ||
        schedule.id?.toLowerCase().includes(query) ||
        schedule.filename?.toLowerCase().includes(query) ||
        schedule.status?.toLowerCase().includes(query) ||
        projectName.includes(query)
      );
    });
  }, [schedules, searchQuery, projectNameMap]);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    schedule: ServicesMasterSchedule,
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSchedule(schedule);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuSchedule(null);
  };

  // DataGrid columns definition
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'rowNumber',
        headerName: 'No.',
        width: 60,
        sortable: false,
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 280,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'project',
        headerName: 'Project',
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) =>
          getProjectDisplayName(params.row.projectId),
      },
      {
        field: 'filename',
        headerName: 'Filename',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'uploadedAt',
        headerName: 'Uploaded At',
        width: 180,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 100,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            key="actions"
            icon={<MoreVertIcon />}
            label="Actions"
            onClick={(event) => handleMenuClick(event, params.row)}
          />,
        ],
      },
    ],
    [projectNameMap],
  );

  // Prepare rows with row numbers
  const rows = useMemo(() => {
    return filteredSchedules.map((schedule, index) => ({
      ...schedule,
      rowNumber: index + 1,
    }));
  }, [filteredSchedules]);

  // Delete handlers
  const handleDeleteClick = (schedule: ServicesMasterSchedule) => {
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setScheduleToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!scheduleToDelete?.id) return;

    setDeleting(true);
    try {
      await deleteSchedule({ scheduleId: scheduleToDelete.id }).unwrap();
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
      await refreshData();
      onDeleteSuccess?.();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
    } finally {
      setDeleting(false);
    }
  };

  // Edit handlers
  const handleEditClick = (schedule: ServicesMasterSchedule) => {
    setScheduleToEdit(schedule);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setScheduleToEdit(null);
  };

  const handleEditSave = async (
    id: string,
    name: string,
    projectId: string,
  ) => {
    setUpdating(true);
    try {
      await updateSchedule({
        scheduleId: id,
        handlersUpdateMasterScheduleRequest: {
          name: name || undefined,
          projectId: projectId || undefined,
        },
      }).unwrap();
      setEditDialogOpen(false);
      setScheduleToEdit(null);
      await refreshData();
      onUpdateSuccess?.();
    } catch (error) {
      console.error('Failed to update schedule:', error);
      onUpdateError?.();
    } finally {
      setUpdating(false);
    }
  };

  // Overwrite handlers
  const handleOverwriteClick = (schedule: ServicesMasterSchedule) => {
    setScheduleToOverwrite(schedule);
    setOverwriteDialogOpen(true);
  };

  const handleOverwriteClose = () => {
    setOverwriteDialogOpen(false);
    setScheduleToOverwrite(null);
  };

  const handleOverwriteUpload = async (file: File) => {
    if (!scheduleToOverwrite?.id) return;

    // Get the project ID from the schedule being replaced
    const projectId = (scheduleToOverwrite as any).projectId || '';
    const scheduleName = scheduleToOverwrite.name || '';

    try {
      // Step 1: Delete the existing schedule
      await deleteSchedule({ scheduleId: scheduleToOverwrite.id }).unwrap();

      // Step 2: Upload the new file with the same project association
      const formData = new FormData();
      formData.append('file', file);
      if (projectId) {
        formData.append('project_id', projectId);
      }
      if (scheduleName) {
        formData.append('project_name', scheduleName);
      }

      await uploadSchedule({ body: formData as any }).unwrap();

      setOverwriteDialogOpen(false);
      setScheduleToOverwrite(null);
      await refreshData();
      onOverwriteSuccess?.();
    } catch (error) {
      console.error('Failed to overwrite schedule:', error);
      alert('Failed to replace schedule. Please try again.');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <>
      {/* DataGrid */}
      <Box sx={{ width: '100%' }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          pagination
          pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          initialState={{
            pagination: {
              paginationModel: { pageSize: DEFAULT_TABLE_PAGE_SIZE },
            },
          }}
          slotProps={{
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderRight: 'none',
            },
            '& .MuiDataGrid-columnHeader': {
              borderRight: 'none',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
          }}
          localeText={{
            noRowsLabel: 'No Master Schedule Data Available',
          }}
        />
      </Box>

      {/* Actions Menu */}
      <Menu
        id="actions-menu"
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'actions-button',
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuSchedule) handleEditClick(menuSchedule);
            handleMenuClose();
          }}
          disabled={updating}
        >
          Edit/Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuSchedule) handleOverwriteClick(menuSchedule);
            handleMenuClose();
          }}
        >
          Replace
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuSchedule) handleDeleteClick(menuSchedule);
            handleMenuClose();
          }}
          disabled={deleting}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        open={deleteDialogOpen}
        title="Delete IMS Schedule"
        onClose={handleDeleteCancel}
        content={{
          mainText: `Are you sure you want to delete "${scheduleToDelete?.name}"? This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      {/* Edit Dialog */}
      <EditDialog
        open={editDialogOpen}
        schedule={scheduleToEdit}
        onClose={handleEditClose}
        onSave={handleEditSave}
      />

      {/* Overwrite Dialog */}
      <OverwriteDialog
        open={overwriteDialogOpen}
        schedule={scheduleToOverwrite}
        onClose={handleOverwriteClose}
        onUpload={handleOverwriteUpload}
      />
    </>
  );
};

export default MppTable;
